import {createActionGroup, emptyProps, props} from '@ngrx/store';
import {
  IAddItemRule,
  IAutoOrderAccountSettings,
  IAutoOrderExpirationSettings,
  IAutoOrderOrderingSettings,
  IDisableStockItemRule,
  IEmailSettings,
  IEnableStockItemRule,
  IGlobalStockTag,
  IMultiplePriceChangeRulesStoreSettings,
  IOpenOrderSettings,
  IPriceBandingStoreSettings,
  IStockItemEditThresholds,
  IStoreActionSetting,
  IStoreOverrideSettings,
  ITableNavSettings,
  SettingsReferenceEnum,
  TCopyableStoreSetting,
  TLineColour,
  TLocaleDateFormat,
  TStockTag,
  TPriceBandingSource,
  TStoreDisplayOrder,
  TSupplierEmail,
} from '../models';
import {IGlobalError} from '../../../shared/shared-components/components/global-error-display/models/global-error';
import {IStore} from '../../../shared/shared-models/store/store';
import {IProcessedOrderSettings} from '../models/auto-ordering/ao-processed-order-settings.interface';
import {IPriceBand} from '../../../shared/shared-models/price-banding/price-band';

//#region [Settings | Initialize] Initialize

export const initSettings = createActionGroup({
  source: 'Settings | Initialize',
  events: {
    ['Start']: emptyProps,
    ['Complete']: emptyProps,
  },
});

//#endregion

//#region [Settings | Selected] Set Selected

export const settingsSetSelected = createActionGroup({
  source: 'Settings | Selected',
  events: {
    ['Set Selected Reference For Navigation']: props<{
      ref: SettingsReferenceEnum;
    }>(),
    ['Set Selected Reference']: props<{
      ref: SettingsReferenceEnum;
    }>(),
    ['Set Selected Section']: props<{
      ref: string;
    }>(),
    ['Set Selected Sub Section']: props<{
      ref: string;
    }>(),
    ['Set Selected Section and Reference']: props<{
      ref: SettingsReferenceEnum;
      section: string;
    }>(),
  },
});

//#endregion

//#region [Settings | User] Get / Set Date Formatting

export const settingUserDateFormatting = createActionGroup({
  source: 'Settings | User',
  events: {
    ['Get Date Formatting']: props<{
      requestId?: string;
    }>(),
    ['Get Date Formatting Success']: props<{
      format: TLocaleDateFormat;
      requestId?: string;
    }>(),
    ['Get Date Formatting Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
    ['Set Date Formatting']: props<{
      format: TLocaleDateFormat;
      requestId?: string;
    }>(),
    ['Set Date Formatting Success']: props<{
      format: TLocaleDateFormat;
      requestId?: string;
    }>(),
    ['Set Date Formatting Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
  },
});

//#endregion

//#region [Settings | User] Get / Set Store Display Order

export const settingUserStoreDisplayOrder = createActionGroup({
  source: 'Settings | User',
  events: {
    ['Get Store Display Order']: props<{
      requestId?: string;
    }>(),
    ['Get Store Display Order Success']: props<{
      order: TStoreDisplayOrder;
      customStoreOrder?: string[];
      requestId?: string;
    }>(),
    ['Get Store Display Order Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
    ['Set Store Display Order']: props<{
      order: TStoreDisplayOrder;
      customStoreOrder?: string[];
      requestId?: string;
    }>(),
    ['Set Store Display Order Success']: props<{
      order: TStoreDisplayOrder;
      customStoreOrder?: string[];
      requestId?: string;
    }>(),
    ['Set Store Display Order Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
  },
});

//#endregion

//#region [Settings | User] Get / Set Fit To Grid

export const settingUserFitToGrid = createActionGroup({
  source: 'Settings | User',
  events: {
    ['Get Fit To Grid']: props<{
      requestId?: string;
    }>(),
    ['Get Fit To Grid Success']: props<{
      fit: boolean;
      requestId?: string;
    }>(),
    ['Get Fit To Grid Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
    ['Set Fit To Grid']: props<{
      fit: boolean;
      requestId?: string;
    }>(),
    ['Set Fit To Grid Success']: props<{
      fit: boolean;
      requestId?: string;
    }>(),
    ['Set Fit To Grid Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
  },
});

//#endregion

//#region [Settings | User] Get / Set Edit Preview Cols NGP Report

export const settingUserEditPreviewColsNGPReport = createActionGroup({
  source: 'Settings | User',
  events: {
    ['Get Edit Preview Cols NGP Report']: props<{
      requestId?: string;
    }>(),
    ['Get Edit Preview Cols NGP Report Success']: props<{
      visibleFields: { [index: number]: string };
      requestId?: string;
    }>(),
    ['Get Edit Preview Cols NGP Report Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
    ['Set Edit Preview Cols NGP Report']: props<{
      visibleFields: { [index: number]: string };
      requestId?: string;
    }>(),
    ['Set Edit Preview Cols NGP Report Success']: props<{
      visibleFields: { [index: number]: string };
      requestId?: string;
    }>(),
    ['Set Edit Preview Cols NGP Report Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
  },
});

//#endregion

//#region [Settings | User] Get / Set Edit Preview Cols Stock

export const settingUserEditPreviewColsStock = createActionGroup({
  source: 'Settings | User',
  events: {
    ['Get Edit Preview Cols Stock']: props<{
      requestId?: string;
    }>(),
    ['Get Edit Preview Cols Stock Success']: props<{
      visibleFields: { [index: number]: string };
      requestId?: string;
    }>(),
    ['Get Edit Preview Cols Stock Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
    ['Set Edit Preview Cols Stock']: props<{
      visibleFields: { [index: number]: string };
      requestId?: string;
    }>(),
    ['Set Edit Preview Cols Stock Success']: props<{
      visibleFields: { [index: number]: string };
      requestId?: string;
    }>(),
    ['Set Edit Preview Cols Stock Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
  },
});

//#endregion

//#region [Settings | User] Get / Set Table Preferences

export const settingUserTablePreferences = createActionGroup({
  source: 'Settings | Table',
  events: {
    ['Get Table Preferences']: props<{
      requestId?: string;
    }>(),
    ['Get Table Preferences Success']: props<{
      settings: ITableNavSettings;
      requestId?: string;
    }>(),
    ['Get Table Preferences Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
    ['Set Table Preferences']: props<{
      settings: ITableNavSettings;
      requestId?: string;
    }>(),
    ['Set Table Preferences Success']: props<{
      settings: ITableNavSettings;
      requestId?: string;
    }>(),
    ['Set Table Preferences Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
  },
});

//#endregion

//#region [Settings | User Store] Get / Set Mailer Configuration

export const settingUserStoreMailerConfiguration = createActionGroup({
  source: 'Settings | User Store',
  events: {
    ['Get Mailer Configuration']: props<{
      requestId?: string;
    }>(),
    ['Get Mailer Configuration Success']: props<{
      mailSettings: IEmailSettings;
      requestId?: string;
    }>(),
    ['Get Mailer Configuration Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
    ['Set Mailer Configuration']: props<{
      mailSettings: IEmailSettings;
      requestId?: string;
    }>(),
    ['Set Mailer Configuration Success']: props<{
      mailSettings: IEmailSettings;
      requestId?: string;
    }>(),
    ['Set Mailer Configuration Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
    ['Get All Users Mailer Configurations']: emptyProps(),
    ['Get All Users Mailer Configurations Success']: props<{
      usersEmailSettings: Record<string, IEmailSettings>;
      usersMailerDocumentExists: Record<string, boolean>;
      store: IStore;
    }>(),
    ['Get All Users Mailer Configurations Failure']: props<{
      error: IGlobalError;
    }>(),
    ['Test Mailer Configuration']: props<{
      authType: 'smtp' | 'microsoft365';
      storeId: string;
      userId: string;
    }>(),
    ['Test Mailer Configuration Success']: props<{
      status: string;
      userId: string;
      storeId: string;
      lastTestAt: string;
      lastTestAuthType: 'smtp' | 'microsoft365';
      lastTestMessage: string;
    }>(),
    ['Test Mailer Configuration Failure']: props<{
      error: IGlobalError;
    }>(),
    ['Set Mailer Auth Type']: props<{
      authType: 'smtp' | 'microsoft365';
      userId: string;
      storeId: string;
    }>(),
    ['Set Mailer Auth Type Success']: props<{
      userId: string;
      storeId: string;
      mailSettings: IEmailSettings;
      mailerDocumentExists?: boolean;
    }>(),
    ['Set Mailer Auth Type Failure']: props<{
      error: IGlobalError;
    }>(),
    ['Connect Microsoft Mail']: props<{
      targetUserId: string;
      loginHint?: string;
    }>(),
    ['Connect Microsoft Mail Success']: props<{
      userId: string;
      storeId: string;
      mailSettings: IEmailSettings;
      mailerDocumentExists: boolean;
    }>(),
    ['Connect Microsoft Mail Failure']: props<{
      error: IGlobalError;
    }>(),
    ['Disconnect Microsoft Mail']: props<{
      targetUserId: string;
    }>(),
    ['Disconnect Microsoft Mail Success']: props<{
      userId: string;
      storeId: string;
      mailSettings: IEmailSettings;
      mailerDocumentExists?: boolean;
    }>(),
    ['Disconnect Microsoft Mail Failure']: props<{
      error: IGlobalError;
    }>(),
    ['Request Microsoft Admin Consent']: emptyProps(),
    ['Request Microsoft Admin Consent Success']: emptyProps(),
    ['Request Microsoft Admin Consent Failure']: props<{
      error: IGlobalError;
    }>(),
    ['Microsoft Mail Cancelled']: emptyProps(),
  },
});

//#endregion

//#region [Settings | User Store] Get / Set Order Configuration

export const settingUserStoreOrderConfiguration = createActionGroup({
  source: 'Settings | User Store',
  events: {
    ['Get Order Configuration']: props<{
      requestId?: string;
    }>(),
    ['Get Order Configuration Success']: props<{
      orderSettings: IAutoOrderOrderingSettings;
      store: IStore;
      requestId?: string;
    }>(),
    ['Get Order Configuration Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
    ['Set Order Configuration']: props<{
      orderSettings: Partial<IAutoOrderOrderingSettings>;
      requestId?: string;
    }>(),
    ['Set Order Configuration Success']: props<{
      orderSettings: Partial<IAutoOrderOrderingSettings>;
      store: IStore;
      requestId?: string;
    }>(),
    ['Set Order Configuration Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
  },
});

//#endregion

//#region [Settings | Store] Get / Set AO Expiration

export const settingStoreAOExpirationSettings = createActionGroup({
  source: 'Settings | Store',
  events: {
    ['Get AO Expiration']: props<{
      params: IStoreActionSetting;
      requestId?: string;
    }>(),
    ['Get AO Expiration Success']: props<{
      params: IStoreActionSetting<IAutoOrderExpirationSettings>;
      errors?: IGlobalError[];
      requestId?: string;
    }>(),
    ['Get AO Expiration Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
    ['Set AO Expiration']: props<{
      settings: IAutoOrderExpirationSettings;
      requestId?: string;
    }>(),
    ['Set AO Expiration Success']: props<{
      settings: IAutoOrderExpirationSettings;
      store: IStore;
      requestId?: string;
    }>(),
    ['Set AO Expiration Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
  },
});

//#endregion

//#region [Settings | Store] Get / Set AO Full Open Order

export const settingStoreAOStoreOpenOrderSettings = createActionGroup({
  source: 'Settings | Store',
  events: {
    ['Get AO Full Open Order']: props<{
      params: IStoreActionSetting;
      requestId?: string;
    }>(),
    ['Get AO Full Open Order Success']: props<{
      params: IStoreActionSetting<IOpenOrderSettings>;
      errors?: IGlobalError[];
      requestId?: string;
    }>(),
    ['Get AO Full Open Order Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
    ['Set AO Full Open Order']: props<{
      settings: IOpenOrderSettings;
      requestId?: string;
    }>(),
    ['Set AO Full Open Order Success']: props<{
      settings: IOpenOrderSettings;
      store: IStore;
      requestId?: string;
    }>(),
    ['Set AO Full Open Order Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
  },
});

//#endregion

//#region [Settings | Store] Get/ Set AO Order Scheduler

export const settingStoreAOStoreOrderScheduler = createActionGroup({
  source: 'Settings | Store',
  events: {
    ['Get AO Order Scheduler']: props<{
      params: IStoreActionSetting;
      requestId?: string;
    }>(),
    ['Get AO Order Scheduler Success']: props<{
      params: IStoreActionSetting<IAutoOrderAccountSettings>;
      errors?: IGlobalError[];
      requestId?: string;
    }>(),
    ['Get AO Order Scheduler Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
    ['Set AO Order Scheduler']: props<{
      settings: IAutoOrderAccountSettings;
      requestId?: string;
    }>(),
    ['Set AO Order Scheduler Success']: props<{
      settings: IAutoOrderAccountSettings;
      store: IStore;
      requestId?: string;
    }>(),
    ['Set AO Order Scheduler Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
  },
});

//#endregion

//#region [Settings | Store] Get / Set AO Supplier Emails

export const settingStoreAOSupplierEmails = createActionGroup({
  source: 'Settings | Store',
  events: {
    ['Get Auto Order Supplier Emails']: props<{
      params: IStoreActionSetting;
      requestId?: string;
    }>(),
    ['Get Auto Order Supplier Emails Success']: props<{
      params: IStoreActionSetting<{ [account: string]: TSupplierEmail[] }>;
      errors?: IGlobalError[];
      requestId?: string;
    }>(),
    ['Get Auto Order Supplier Emails Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
    ['Set Auto Order Supplier Emails']: props<{
      emails: { [account: string]: TSupplierEmail[] };
      requestId?: string;
    }>(),
    ['Set Auto Order Supplier Emails Success']: props<{
      emails: { [account: string]: TSupplierEmail[] };
      store: IStore;
      requestId?: string;
    }>(),
    ['Set Auto Order Supplier Emails Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
  },
});

//#endregion

//#region [Settings | Store] Get / Set Stock Edit Threshold Rules

export const settingStoreStockEditThresholdRules = createActionGroup({
  source: 'Settings | Store',
  events: {
    ['Get Stock Edit Threshold Rules']: props<{
      params: IStoreActionSetting;
      requestId?: string;
    }>(),
    ['Get Stock Edit Threshold Rules Success']: props<{
      params: IStoreActionSetting<IStockItemEditThresholds>;
      errors?: IGlobalError[];
      requestId?: string;
    }>(),
    ['Get Stock Edit Threshold Rules Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
    ['Set Stock Edit Threshold Rules']: props<{
      rules: IStockItemEditThresholds;
      requestId?: string;
    }>(),
    ['Set Stock Edit Threshold Rules Success']: props<{
      store: IStore;
      rules: IStockItemEditThresholds;
      requestId?: string;
    }>(),
    ['Set Stock Edit Threshold Rules Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
  },
});

//#endregion

//#region [Settings | Store] Get / Set Price Bands

export const settingStorePriceBands = createActionGroup({
  source: 'Settings | Store',
  events: {
    ['Get Price Bands']: props<{
      params: IStoreActionSetting;
      requestId?: string;
    }>(),
    ['Get Price Bands Success']: props<{
      globalBands: IPriceBand[];
      params: IStoreActionSetting<IPriceBandingStoreSettings>;
      errors?: IGlobalError[];
      fetchStartedAt?: number;
      requestId?: string;
    }>(),
    ['Get Price Bands Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
    ['Set Price Bands']: props<{
      bands: IPriceBand[];
      requestId?: string;
    }>(),
    ['Set Price Bands Success']: props<{
      bands: IPriceBand[];
      source: TPriceBandingSource;
      store: IStore;
      requestId?: string;
      savedAt?: number;
    }>(),
    ['Set Price Bands Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
  },
});

//#endregion

//#region [Settings | Store] Get / Set Multiple Price Change Rules

export const settingStoreMultiplePriceChangeRules = createActionGroup({
  source: 'Settings | Store',
  events: {
    ['Get Multiple Price Change Rules']: props<{
      params: IStoreActionSetting;
      requestId?: string;
    }>(),
    ['Get Multiple Price Change Rules Success']: props<{
      params: IStoreActionSetting<IMultiplePriceChangeRulesStoreSettings>;
      errors?: IGlobalError[];
      requestId?: string;
    }>(),
    ['Get Multiple Price Change Rules Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
    ['Set Multiple Price Change Rules']: props<{
      settings: IMultiplePriceChangeRulesStoreSettings;
      requestId?: string;
    }>(),
    ['Set Multiple Price Change Rules Success']: props<{
      settings: IMultiplePriceChangeRulesStoreSettings;
      store: IStore;
      requestId?: string;
      savedAt?: number;
    }>(),
    ['Set Multiple Price Change Rules Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
  },
});

//#endregion

//#region [Settings | Store] Copy Store Settings

export const settingStoreCopySettings = createActionGroup({
  source: 'Settings | Store',
  events: {
    ['Copy Store Settings']: props<{
      settings: TCopyableStoreSetting[];
      sourceStore: IStore;
      targetStore: IStore;
      requestId?: string;
    }>(),
    ['Copy Store Settings Success']: props<{
      settings: TCopyableStoreSetting[];
      sourceStore: IStore;
      targetStore: IStore;
      priceBands?: IPriceBand[];
      requestId?: string;
      savedAt?: number;
    }>(),
    ['Copy Store Settings Failure']: props<{
      error: IGlobalError;
      settings: TCopyableStoreSetting[];
      targetStore: IStore;
      requestId?: string;
    }>(),
    ['Clear Copy Store Settings Result']: emptyProps(),
  },
});

//#endregion

//#region [Settings | Store] Get / Set Stock Item Add Rules

export const settingStoreStockItemAddRule = createActionGroup({
  source: 'Settings | Store',
  events: {
    ['Get Add Item Rules']: props<{
      params: IStoreActionSetting;
      requestId?: string;
    }>(),
    ['Get Add Item Rules Success']: props<{
      params: IStoreActionSetting<IAddItemRule>;
      errors?: IGlobalError[];
      requestId?: string;
    }>(),
    ['Get Add Item Rules Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
    ['Set Add Item Rules']: props<{
      rules: IAddItemRule;
      requestId?: string;
    }>(),
    ['Set Add Item Rules Success']: props<{
      store: IStore;
      rules: IAddItemRule;
      requestId?: string;
    }>(),
    ['Set Add Item Rules Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
  },
});

//#endregion

//#region [Settings | Store] Get / Set Stock Item Enable and Disable Rules
export const settingStockItemEnableRules = createActionGroup({
  source: 'Settings | Store',
  events: {
    ['Get Enable Stock Item Rule']: props<{
      params: IStoreActionSetting;
      requestId?: string;
    }>(),
    ['Get Enable Stock Item Rule Success']: props<{
      params: IStoreActionSetting<IEnableStockItemRule>;
      errors?: IGlobalError[];
      requestId?: string;
    }>(),
    ['Get Enable Stock Item Rule Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
    ['Set Enable Stock Item Rule']: props<{
      settings: IEnableStockItemRule;
      requestId?: string;
    }>(),
    ['Set Enable Stock Item Rule Success']: props<{
      settings: IEnableStockItemRule;
      store: IStore;
      requestId?: string;
    }>(),
    ['Set Enable Stock Item Rule Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
    ['Get Disable Stock Item Rule']: props<{
      params: IStoreActionSetting;
      requestId?: string;
    }>(),
    ['Get Disable Stock Item Rule Success']: props<{
      params: IStoreActionSetting<IDisableStockItemRule>;
      errors?: IGlobalError[];
      requestId?: string;
    }>(),
    ['Get Disable Stock Item Rule Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
    ['Set Disable Stock Item Rule']: props<{
      settings: IDisableStockItemRule;
      requestId?: string;
    }>(),
    ['Set Disable Stock Item Rule Success']: props<{
      settings: IDisableStockItemRule;
      store: IStore;
      requestId?: string;
    }>(),
    ['Set Disable Stock Item Rule Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
  },
});

//#endregion

//#region [Settings | Store] Get / Set Store Overrides

export const settingStoreOverrides = createActionGroup({
  source: 'Settings | Store',
  events: {
    ['Get Store Override']: props<{
      params: IStoreActionSetting;
      requestId?: string;
    }>(),
    ['Get Store Override Success']: props<{
      params: IStoreActionSetting<IStoreOverrideSettings>;
      errors?: IGlobalError[];
      requestId?: string;
    }>(),
    ['Get Store Override Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
    ['Set Store Override']: props<{
      overrides: IStoreOverrideSettings;
      requestId?: string;
    }>(),
    ['Set Store Override Success']: props<{
      overrides: IStoreOverrideSettings;
      store: IStore;
      requestId?: string;
    }>(),
    ['Set Store Override Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
  },
});

//#endregion

//#region [Settings | Store] Get / Set Auto Order Processed Order Settings

export const settingStoreProcessedOrder = createActionGroup({
  source: 'Settings | Store',
  events: {
    ['Get Store Processed Order']: props<{
      params: IStoreActionSetting;
      requestId?: string;
    }>(),
    ['Get Store Processed Order Success']: props<{
      params: IStoreActionSetting<IProcessedOrderSettings>;
      errors?: IGlobalError[];
      requestId?: string;
    }>(),
    ['Get Store Processed Order Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
    ['Set Store Processed Order']: props<{
      processedOrder: IProcessedOrderSettings;
      requestId?: string;
    }>(),
    ['Set Store Processed Order Success']: props<{
      processedOrder: IProcessedOrderSettings;
      store: IStore;
      requestId?: string;
    }>(),
    ['Set Store Processed Order Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
  },
});

//#endregion

//#region [Settings | Store] Get / Set Store Tags

export const settingStoreTagsSettings = createActionGroup({
  source: 'Settings | Store',
  events: {
    ['Get Store Tags']: props<{
      params: IStoreActionSetting;
      requestId?: string;
    }>(),
    ['Get Store Tags Success']: props<{
      params: IStoreActionSetting<TStockTag[]>;
      errors?: IGlobalError[];
      requestId?: string;
    }>(),
    ['Get Store Tags Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
    ['Set Store Tags']: props<{
      tags: IGlobalStockTag[];
      requestId?: string;
    }>(),
    ['Set Store Tags Success']: props<{
      store: IStore;
      tags: TStockTag[];
      requestId?: string;
    }>(),
    ['Set Store Tags Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
  },
});

//#endregion

//#region [Settings | Shared] Get Line Colours

export const settingSharedLineColours = createActionGroup({
  source: 'Settings | Shared',
  events: {
    ['Get Line Colours']: props<{
      params: IStoreActionSetting;
      requestId?: string;
    }>(),
    ['Get Line Colours Success']: props<{
      params: IStoreActionSetting<TLineColour>;
      errors?: IGlobalError[];
      requestId?: string;
    }>(),
    ['Get Line Colours Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
  },
});

//#endregion


//#region [Settings | Global] Get / Set Global Tags

export const settingGlobalTagsSettings = createActionGroup({
  source: 'Settings | Global',
  events: {
    ['Get Global Tags']: props<{
      params: IStoreActionSetting;
      requestId?: string;
    }>(),
    ['Get Global Tags Success']: props<{
      tags: TStockTag[];
      requestId?: string;
    }>(),
    ['Get Global Tags Failure']: props<{
      error: IGlobalError;
      requestId?: string;
    }>(),
  },
});

//#endregion

/**
 * ============================================================================
 * MIGRATION STATUS: OLD SETTINGS → NEW SETTINGS
 * ============================================================================
 *
 * This section documents the migration status from the old settings implementation
 * (src/app/features/settings) to the new settings implementation (settings-new).
 *
 * CRITICAL MISSING FEATURES (must be implemented before deprecating old settings):
 * ---------------------------------------------------------------------------------
 *
 * 1. ❌ EMAIL TEST FUNCTIONALITY
 *    - Old Location: src/app/features/settings/store/settings.effects.ts:1153-1309
 *    - Status: Interface exists (IEmailTester), component folder exists but empty
 *    - Required:
 *      • Create effect to test email configuration
 *      • Create test document in Firestore (collection: users/{userId}/stores/{storeId}/settings/email-test)
 *      • Monitor status with Firebase snapshot listener (onSnapshot)
 *      • Display success/failure modal based on status ('SENT' vs 'FAILED')
 *      • Complete feature-email-tester-settings component
 *
 * 2. ❌ MAINTENANCE MODE MONITORING
 *    - Old Location: src/app/features/settings/store/settings.effects.ts:1514-1558
 *    - Status: Not implemented
 *    - Required:
 *      • Monitor Firestore collection: admin/communication/maintenance
 *      • Show modal when maintenance document exists
 *      • Display maintenance end date
 *      • Auto-dismiss modal and reload when maintenance ends
 *      • Create maintenance monitoring effect
 *
 * 3. ✅ PRICE BAND MANAGEMENT WITH GLOBAL FALLBACK
 *    - New Location: settingStorePriceBands + FeatureStockPriceBandingComponent
 *    - Status: Implemented in settings-new
 *    - Behaviour:
 *      • Get/Set price bands per store at stores/{storeId}/settings/price_bands
 *      • Fall back to global/settings/shared_settings/price_bands when the store has none
 *      • Saving creates or updates the store-specific copy
 *
 * VALIDATION RULES NEEDING VERIFICATION:
 * ---------------------------------------------------------------------------------
 *
 * 4. ⚠️ SMTP/IMAP PORT VALIDATION
 *    - Old Implementation: Range validation 1-65535 enforced
 *    - New Implementation: Needs verification in user-profile-settings
 *    - Action: Verify port range validation is present
 *
 * 5. ⚠️ EMAIL REGEX PATTERN
 *    - Old Implementation: Custom regex /^[^\s@]+@[^\s@]+\.[^\s@]+$/
 *    - New Implementation: Uses Validators.email (Angular built-in)
 *    - Action: Determine if built-in validator is sufficient or custom regex needed
 *
 * COMPLETED IMPLEMENTATIONS:
 * ---------------------------------------------------------------------------------
 *
 * ✅ Price Threshold Validation (0-100%) - feature-stock-price-threshold.component.ts:200-201
 * ✅ On-hold Code Bitwise Operations - feature-stock-rule-disable.component.ts:255,223,227
 * ✅ Stock Tag Validation - feature-stock-tags.component.ts:216-245
 * ✅ Auto-sync Disabling→Enabling Rules - setting.effects.ts:1527-1591 (JUST IMPLEMENTED)
 * ✅ User Date Formatting - NEW FEATURE
 * ✅ Store Display Order - NEW FEATURE
 * ✅ Comprehensive Auto-Ordering Settings - NEW FEATURES (5 components vs 1)
 * ✅ Enhanced Stock Tags with CRUD - NEW FEATURE
 * ✅ Unsaved Changes Detection - NEW FEATURE
 * ✅ Settings Navigation System - NEW FEATURE
 *
 * VERIFICATION RESULTS (Completed 2025-12-05):
 * ---------------------------------------------------------------------------------
 *
 * Overall Implementation Quality: B+ (85%)
 * - 16 components verified
 * - 12 components (75%) fully functional and production-ready
 * - 3 components (19%) have bugs requiring fixes
 * - 1 component (6%) not implemented (placeholder)
 *
 * CRITICAL BUGS FOUND (MUST FIX):
 *   1. ❌ feature-auto-order-open-order-settings - NOT IMPLEMENTED (placeholder only)
 *   2. 🐛 feature-auto-order-order-settings:100,107,114,122 - Wrong error variables
 *   3. 🐛 feature-auto-order-order-settings:343 - Patch generation logic bug
 *   4. 🐛 feature-shelf-talkers-general:140-142 - Saves entire object instead of changed field
 *   5. ⚠️ user-table-settings - Missing IHasUnsavedChanges interface implementation
 *
 * See: C:\Projects\gallix_fe\SETTINGS_VERIFICATION_REPORT.md for full details
 *
 * MIGRATION PLAN:
 * ---------------------------------------------------------------------------------
 *
 * Phase 1: Complete Missing Critical Features
 *   - Implement Email Test Functionality
 *   - Implement Maintenance Mode Monitoring
 *   - Implement Price Band Management with Global Fallback
 *   - Verify all validation rules
 *
 * Phase 2: Verify All Implemented Settings ✅ COMPLETED 2025-12-05
 *   - ✅ Verified all 16 components
 *   - ✅ Documented all bugs and issues
 *   - ✅ Created comprehensive verification report
 *   - ⏳ NEXT: Fix 5 critical bugs before migration
 *
 * Phase 2.5: Fix Critical Bugs (CURRENT PHASE - BEFORE MIGRATION)
 *   - Fix feature-auto-order-order-settings error variable bugs (4 locations)
 *   - Fix feature-auto-order-order-settings patch generation logic
 *   - Fix feature-shelf-talkers-general save logic
 *   - Implement feature-auto-order-open-order-settings
 *   - Implement IHasUnsavedChanges in user-table-settings
 *   - Add form field disabling based on user access (2 components)
 *   - Test all fixes thoroughly
 *
 * Phase 3: Remove Old Settings References
 *   - Update routing to use new settings
 *   - Remove old settings components from imports
 *   - Update any links/navigation to point to new settings
 *   - Remove old settings module
 *
 * Phase 4: Clean Up
 *   - Delete old settings directory (src/app/features/settings)
 *   - Update documentation
 *   - Remove any old settings-related code
 *   - Celebrate! 🎉
 *
 * Phase 5: New settings not yet implemented on Gallix (FINAL PHASE)
 *   - Local storage service and settings
 *    - all local storage to run through this service through state
 *    - user should be able to delete and remove and clear certain storages
 *   - Clear cache and hard refresh buttons
 *   - Technical support button
 *   - Implement theme correctly
 *   - Make a list and task all technical debt
 *
 * ============================================================================
 */

//#region [Settings | Price Bands] Get / Set

/**
 * TODO TECHNICAL DEBT: Complete Price Bands actions
 * This will include get/set operations for price band configurations
 * See CRITICAL MISSING FEATURES #3 above for detailed requirements
 */

//#endregion

//#region [Settings | Shelf Talkers] Get / Set

/**
 * TODO TECHNICAL DEBT: Complete Shelf Talkers actions
 * This will include get/set operations for shelf talker configurations
 */

//#endregion

//#region [Settings | Saved Preferences] Get / Set

/**
 * TODO TECHNICAL DEBT: Complete Saved Preferences actions
 * This will include get/set operations for user-saved preferences
 */

//#endregion

//#region [Settings | Store Advanced Filters] Get / Set

/**
 * TODO TECHNICAL DEBT: Complete Store Advanced Filters actions
 * This will include get/set operations for advanced filter configurations
 */

//#endregion
